import {
  clearNotesCourseQuery,
  getLandingNotesCourses,
  getLandingNotesCoursesByPage,
  setNotesData,
} from '@/actions/notes';
import { capitalizeFirstLetter } from '@/helpers/dataHelper';
import useWidth, { IBooleanArgs } from '@/helpers/widthUtil';
import { ArrowRightOutlined } from '@ant-design/icons';
import { Card, Skeleton } from 'antd';
import Link from 'next/link';
import { FC, RefCallback, useCallback, useEffect, useRef, useState } from 'react';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import { Col, Row, Spinner } from 'reactstrap';
import { Dispatch } from 'redux';
import NoteSidebar from './NoteSidebar';

const NoteCourseLanding: FC<{}> = () => {
  const dispatch: Dispatch = useDispatch();
  const { isMobile }: IBooleanArgs = useWidth();
  const courseObserver = useRef<any>();

  const {
    courses,
    categories,
    getMoreCoursesStatus,
    courseQuery,
    courseMeta,
    loading,
  } = useSelector((state: RootStateOrAny) => ({
    courses: state.notes?.landingNoteCourses?.data,
    categories: state.notes?.landingNoteCourses?.category,
    courseMeta: state?.notes?.landingNoteCourses?.meta,
    getMoreCoursesStatus: state?.notes?.loader?.getMoreLandingCourseStatus,
    loading: state?.notes?.loader?.landingNoteCourseLoading,
    courseQuery: state?.notes?.query?.noteCoursesLanding,
  }));

  const [hasMoreCourse, setHasMoreCourse] = useState<boolean>(false);

  useEffect(() => {
    dispatch(getLandingNotesCourses(courseQuery));
    return () => {
      setHasMoreCourse(false);
      dispatch(clearNotesCourseQuery());
    };
  }, []);

  useEffect(() => {
    setHasMoreCourse(courseMeta?.last_pages === courseQuery?.page ? false : true);
  }, [courseMeta]);

  const lastCourseElementRef: RefCallback<HTMLDivElement> = useCallback(
    (node) => {
      if (loading || getMoreCoursesStatus === 'request') return;
      if (courseObserver.current) courseObserver.current.disconnect();
      courseObserver.current = new IntersectionObserver((entries) => {
        if (
          entries[0].isIntersecting &&
          hasMoreCourse &&
          courseMeta?.last_pages >= courseQuery?.page
        ) {
          dispatch(
            setNotesData({
              name: 'query',
              key: 'noteCoursesLanding',
              value: {
                ...courseQuery,
                page: courseMeta?.current_page + 1,
              },
            })
          );
          dispatch(
            getLandingNotesCoursesByPage({
              data: {
                ...courseQuery,
                page: courseMeta.current_page + 1,
              },
            })
          );
        }
      });
      if (node) courseObserver.current.observe(node);
    },
    [getMoreCoursesStatus === 'request', loading, hasMoreCourse]
  );

  return (
    <>
      <section className="mock-test-grid-area mt-4 mb-5">
        <div className="custom-container">
          <Row>
            <Col md="4" lg="3" sm="4">
              <NoteSidebar categories={categories} />
            </Col>
            <Col lg="9" md="8" sm="8">
              <Row>
                {loading ? (
                  [...Array(1, 2, 3, 4, 5, 6)]?.map((i: number) => (
                    <Col lg="4" md="6" key={i}>
                      <div
                        className="course-item blog mb-5 shadow-sm"
                        style={{
                          borderRadius: '15px',
                        }}
                      >
                        <div
                          className="course-image"
                          style={{
                            background: 'unset',
                          }}
                        >
                          <Skeleton.Image
                            style={{
                              height: '180px',
                              borderTopLeftRadius: '15px',
                              borderTopRightRadius: '15px',
                            }}
                            className="w-100"
                          />
                        </div>
                        <div className="course-content p-2">
                          <div className="d-flex flex-column text-center ">
                            <Skeleton.Button active className="mb-1" />
                            <Skeleton.Button active style={{ width: '100px' }} />
                          </div>
                        </div>
                      </div>
                    </Col>
                  ))
                ) : courses?.length !== 0 ? (
                  courses?.map((course, i) => (
                    <Col
                      lg="4"
                      md="6"
                      key={i}
                      className={`${isMobile ? 'mb-4' : 'mb-5'}`}
                    >
                      <div
                        ref={courses?.length === i + 1 ? lastCourseElementRef : null}
                      >
                        <Link href={`/notes/${course.slug}/subjects`}>
                          <a>
                            <Card
                              className={`course-item-card mock-test-course border`}
                              hoverable
                              style={{
                                borderRadius: '15px',
                              }}
                              cover={
                                <img
                                  alt={course?.name}
                                  style={{
                                    height: '180px',
                                    borderTopLeftRadius: '15px',
                                    borderTopRightRadius: '15px',
                                  }}
                                  src={
                                    course.image
                                      ? course.image
                                      : '/images/notesplaceholder.png'
                                  }
                                />
                              }
                            >
                              <Card.Meta
                                className="d-flex align-items-center justify-content-center"
                                style={{
                                  fontStyle: 'Poppins,sans-serif',
                                }}
                                title={
                                  <span className="text-wrap course-single-name d-flex">
                                    {capitalizeFirstLetter(course?.name)}
                                    <br />
                                  </span>
                                }
                              />
                              <div className="d-flex align-items-center justify-content-center">
                                {course?.subject_no > 1 && (
                                  <>
                                    <span className="pr-2">
                                      {course?.subject_no} Subjects
                                    </span>{' '}
                                    <i
                                      className="fas fa-circle"
                                      style={{ fontSize: '7px' }}
                                    />
                                  </>
                                )}
                                <span className="pl-2">
                                  {' '}
                                  {course?.category?.name}
                                </span>
                              </div>
                              <div
                                className="go-to-course float-right position-absolute"
                                style={{ bottom: '3%', right: '6%' }}
                              >
                                <ArrowRightOutlined
                                  rev=""
                                  style={{ fontSize: '20px', color: '#1c599f' }}
                                />
                              </div>
                            </Card>
                          </a>
                        </Link>
                      </div>
                    </Col>
                  ))
                ) : (
                  <span className="text-center">
                    <p>
                      <img
                        src="/images/no_results.webp"
                        height="250"
                        width="250"
                        className="mb-1"
                      />
                      <br />
                      No notes found.
                      <br />
                    </p>
                  </span>
                )}
                {getMoreCoursesStatus === 'request' && (
                  <div className="d-flex justify-content-center align-items-center">
                    <Spinner animation="border" className="mt-3" />
                  </div>
                )}
              </Row>
            </Col>
          </Row>
        </div>
      </section>
    </>
  );
};

export default NoteCourseLanding;
